import * as React from 'react';
import Box from '@mui/material/Box';
import { useParams, useHistory } from 'react-router-dom';
import { getDownloadURL, ref as ref_storage, uploadBytesResumable } from 'firebase/storage';
import { useState, useEffect } from 'react';
import { storage } from '../../Firebase/firebase.config';
import { FiUpload, FiCheckCircle } from 'react-icons/fi';
import { GrLinkNext } from 'react-icons/gr';
import Button from 'react-bootstrap/Button';
import { getDatabase, ref, set, onValue } from 'firebase/database';

import { styled } from '@mui/material/styles';
import SideNav from '../SideNav';

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

// Shared font stack — matches Article.js styling conventions.
const FONT_STACK = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";

const ReceivePayment = () => {
    const { id } = useParams();
    const history = useHistory();
    const database = getDatabase();

    const [qrFile, setQrFile] = useState(null);
    const [qrUrl, setQrUrl] = useState('');
    const [upiId, setUpiId] = useState('');
    const [payeeName, setPayeeName] = useState('');
    const [bankDetails, setBankDetails] = useState('');
    const [status, setStatus] = useState('idle'); // idle | uploading | done | error

    // Fetch existing payment info
    useEffect(() => {
        onValue(ref(database, `ReceivePayment/${id}`), (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setQrUrl(data.qrUrl || '');
                setUpiId(data.upiId || '');
                setPayeeName(data.payeeName || '');
                setBankDetails(data.bankDetails || '');
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const savePaymentInfo = (extra = {}) => {
        set(ref(database, `ReceivePayment/${id}`), {
            qrUrl,
            upiId,
            payeeName,
            bankDetails,
            ...extra,
        });
    };

    const uploadQr = () => {
        if (!qrFile) {
            alert('Please choose a QR code image to upload.');
            return;
        }

        setStatus('uploading');

        const imgRef = ref_storage(storage, `ReceivePayment/${id}/QR`);
        const uploadTask = uploadBytesResumable(imgRef, qrFile);

        uploadTask
            .then((snapshot) => getDownloadURL(snapshot.ref))
            .then((url) => {
                setQrUrl(url);
                savePaymentInfo({ qrUrl: url });
                setStatus('done');
            })
            .catch((error) => {
                console.error('Error uploading QR code:', error);
                setStatus('error');
                alert('Upload failed. Please try again.');
            });
    };

    const saveDetails = () => {
        if (!upiId.trim() && !bankDetails.trim() && !qrUrl) {
            alert('Please add a QR code, UPI ID, or bank details before saving.');
            return;
        }
        savePaymentInfo();
        alert('Payment details saved.');
    };

    const handleNext = () => {
        history.push(`/article/${id}`);
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <SideNav id={id} />

            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Box component="main" sx={{ flexGrow: 1, p: 3, maxWidth: '700px', boxSizing: 'border-box' }}>
                    <DrawerHeader />

                    <div style={{ fontFamily: FONT_STACK }}>
                        <h1 style={{ color: '#126ca8', fontWeight: 700, textAlign: 'center', marginBottom: '1.75rem' }}>
                            Receive Payment
                        </h1>

                        {/* ── QR CODE UPLOAD ── */}
                        <div
                            style={{
                                border: '1px solid #e2e8f0',
                                padding: '1.5rem',
                                borderRadius: 15,
                                marginBottom: '1.75rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                background: '#f9f9f9',
                                boxSizing: 'border-box',
                            }}
                        >
                            <h4 style={{ fontWeight: 600, color: '#1e293b', marginBottom: '0.4rem' }}>
                                Upload Your Payment QR Code
                            </h4>
                            <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>
                                Patients will scan this to pay you directly (e.g. UPI / GPay / PayTM QR).
                            </p>

                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1.5px dashed #cbd5e1',
                                    borderRadius: '10px',
                                    padding: '0.65rem 0.9rem',
                                    cursor: 'pointer',
                                    fontSize: '0.88rem',
                                    color: '#64748b',
                                    background: '#fff',
                                    marginBottom: '0.9rem',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setQrFile(e.target.files[0])}
                                    style={{ display: 'none' }}
                                />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {qrFile ? qrFile.name : 'Choose QR code image…'}
                                </span>
                            </label>

                            <Button
                                onClick={uploadQr}
                                disabled={status === 'uploading'}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#0073cf',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.4rem',
                                }}
                            >
                                {status === 'uploading' ? (
                                    'Uploading…'
                                ) : status === 'done' ? (
                                    <>Uploaded <FiCheckCircle /></>
                                ) : (
                                    <>Upload QR <FiUpload /></>
                                )}
                            </Button>

                            {qrUrl ? (
                                <img
                                    src={qrUrl}
                                    alt="Payment QR"
                                    style={{
                                        width: 160,
                                        height: 160,
                                        marginTop: '1.25rem',
                                        objectFit: 'contain',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 10,
                                        background: '#fff',
                                        padding: 8,
                                    }}
                                />
                            ) : (
                                <p style={{ marginTop: '1rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                                    No QR code uploaded
                                </p>
                            )}
                        </div>

                        {/* ── UPI / BANK DETAILS ── */}
                        <div
                            style={{
                                border: '1px solid #e2e8f0',
                                padding: '1.5rem',
                                borderRadius: 15,
                                background: '#f9f9f9',
                                boxSizing: 'border-box',
                            }}
                        >
                            <h4 style={{ fontWeight: 600, color: '#1e293b', textAlign: 'center', marginBottom: '1rem' }}>
                                Payment Details (Optional)
                            </h4>

                            <input
                                type="text"
                                placeholder="Payee Name"
                                value={payeeName}
                                onChange={(e) => setPayeeName(e.target.value)}
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '0.65rem 0.9rem',
                                    borderRadius: 10,
                                    border: '1px solid #e2e8f0',
                                    marginBottom: '0.75rem',
                                    fontFamily: FONT_STACK,
                                    fontSize: '0.9rem',
                                }}
                            />

                            <input
                                type="text"
                                placeholder="UPI ID (e.g. name@upi)"
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '0.65rem 0.9rem',
                                    borderRadius: 10,
                                    border: '1px solid #e2e8f0',
                                    marginBottom: '0.75rem',
                                    fontFamily: FONT_STACK,
                                    fontSize: '0.9rem',
                                }}
                            />

                            <textarea
                                rows={4}
                                placeholder="Bank account details (optional) — account no., IFSC, bank name…"
                                value={bankDetails}
                                onChange={(e) => setBankDetails(e.target.value)}
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '0.65rem 0.9rem',
                                    borderRadius: 10,
                                    border: '1px solid #e2e8f0',
                                    marginBottom: '1rem',
                                    fontFamily: FONT_STACK,
                                    fontSize: '0.9rem',
                                    resize: 'vertical',
                                }}
                            />

                            <Button
                                onClick={saveDetails}
                                style={{
                                    width: '100%',
                                    backgroundColor: '#0073cf',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                }}
                            >
                                Save Payment Details <GrLinkNext />
                            </Button>
                        </div>

                        <Button
                            variant="primary"
                            onClick={handleNext}
                            style={{
                                width: '6rem',
                                marginTop: '2rem',
                                marginLeft: '0',
                                borderRadius: '10px',
                                fontWeight: 600,
                            }}
                        >
                            Next
                        </Button>
                    </div>
                </Box>
            </div>
        </Box>
    );
};

export default ReceivePayment;